import { AlertTriangle, PlugZap } from 'lucide-react';
import type { BlocResultats, IndicateurResultat, TableauIndicateurs } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, dateFr, dateHeureFr, montant, nombre, pct } from '@/lib/utils';

/** Valeur d'un indicateur selon son unité. */
const valeur = (i: IndicateurResultat): string => {
  if (i.valeur === null) return '—';
  if (i.unite === 'usd') return montant(i.valeur);
  if (i.unite === 'pourcentage') return pct(i.valeur);
  return nombre(i.valeur);
};

const periode = (i: IndicateurResultat): string | null => {
  if (i.periodeDebut && i.periodeFin) return `du ${dateFr(i.periodeDebut)} au ${dateFr(i.periodeFin)}`;
  if (i.periodeFin) return `au ${dateFr(i.periodeFin)}`;
  return null;
};

const ETAT: Record<IndicateurResultat['etat'], { libelle: string; classe: string }> = {
  NON_CONNECTE: { libelle: 'Non connecté', classe: 'border-dashed text-muted-foreground' },
  A_JOUR: { libelle: 'À jour', classe: '' },
  OBSOLETE: { libelle: 'Obsolète', classe: 'border-amber-300' },
  EN_ERREUR: { libelle: 'En erreur', classe: 'border-red-300' },
};

function Tuile({ i }: { i: IndicateurResultat }) {
  const e = ETAT[i.etat];
  return (
    <details className={cn('group rounded-lg border bg-card p-3', e.classe)} data-testid="indicateur" data-code={i.code} data-etat={i.etat}>
      <summary className="cursor-pointer list-none">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{i.libelle}</div>
        <div className={cn('num mt-1 text-2xl font-semibold', i.etat === 'NON_CONNECTE' && 'text-muted-foreground')}>{i.etat === 'NON_CONNECTE' ? 'Non connecté' : valeur(i)}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          {i.etat === 'NON_CONNECTE' ? (
            <span className="inline-flex items-center gap-1">
              <PlugZap className="size-3" /> {i.reserve ? 'à définir avec la source' : 'en attente de la source'}
            </span>
          ) : (
            <>
              {periode(i) && <span>{periode(i)}</span>}
              {i.actualiseLe && <span>· actualisé le {dateFr(i.actualiseLe)}</span>}
            </>
          )}
          {i.etat === 'OBSOLETE' && (
            <Badge variant="outline" className="border-amber-400 text-amber-800">
              {e.libelle}
            </Badge>
          )}
          {i.etat === 'EN_ERREUR' && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="size-3" /> {e.libelle}
            </Badge>
          )}
          {i.demonstration && <Badge variant="secondary">démonstration</Badge>}
        </div>
      </summary>
      <div className="mt-2 space-y-1 border-t pt-2 text-xs text-muted-foreground">
        <div>{i.definition}</div>
        <div>
          Code <span className="num">{i.code}</span> · actualisation attendue tous les {i.frequenceJours} jours
          {i.actualiseLe && ` · dernière donnée le ${dateHeureFr(i.actualiseLe)}`}
        </div>
        {i.message && <div className="text-red-700">{i.message}</div>}
        {i.ventilations.length > 0 && (
          <ul className="grid gap-x-4 sm:grid-cols-2">
            {i.ventilations.map((v) => (
              <li key={`${v.dimension}-${v.cle}`} className="flex justify-between gap-2">
                <span>
                  {v.dimension} · {v.libelle ?? v.cle}
                </span>
                <span className="num">{i.unite === 'usd' ? montant(v.valeur) : nombre(v.valeur)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function Bloc({ bloc, compact }: { bloc: BlocResultats; compact: boolean }) {
  const s = bloc.source;
  return (
    <Card data-testid={`resultats-${bloc.code}`}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {bloc.libelle}
          {s.demonstration && <Badge variant="secondary">données de démonstration</Badge>}
        </CardTitle>
        <CardDescription>
          Source : {s.libelle} · {s.derniereReception ? `dernière réception le ${dateHeureFr(s.derniereReception)}` : 'aucune réception à ce jour'} · {s.aJour} à jour, {s.obsoletes} obsolète(s), {s.enErreur} en erreur, {s.nonConnectes} non connecté(s). Statistiques institutionnelles agrégées, sans donnée nominative.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {bloc.familles.map((f) => (
          <div key={f.libelle}>
            <div className="mb-2 text-sm font-medium">{f.libelle}</div>
            <div className={cn('grid gap-3', compact ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4')}>
              {(compact ? f.indicateurs.filter((i) => i.etat !== 'NON_CONNECTE').slice(0, 4) : f.indicateurs).map((i) => (
                <Tuile key={i.code} i={i} />
              ))}
              {compact && f.indicateurs.every((i) => i.etat === 'NON_CONNECTE') && <div className="text-xs text-muted-foreground">Non connecté.</div>}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Blocs « Résultats » du tableau de bord (spécifications complémentaires v1.1, § 4) : Études et Accès à la justice
 * (Centralized), Réparations (logiciel de suivi). Présentation complète pour la Direction Générale, synthétique ailleurs.
 */
export function BlocsResultats({ tableau, compact = false }: { tableau: TableauIndicateurs; compact?: boolean }) {
  return (
    <>
      {tableau.blocs.map((b) => (
        <Bloc key={b.code} bloc={b} compact={compact} />
      ))}
    </>
  );
}
